import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/base/dialog'
import { ButtonDnD } from '@/components/ButtonDnD'
import { ButtonMicrophone } from '@/components/ButtonMicrophone'
import { ButtonRaiseHand } from '@/components/ButtonRaiseHand'
import { ButtonShareScreen } from '@/components/ButtonShareScreen'
import { ButtonVideo } from '@/components/ButtonVideo'
import { useSelfDNDButtonState } from '@/hooks/useSelfDNDButtonState'
import { useSelfMicrophoneButtonState } from '@/hooks/useSelfMicrophoneButtonState'
import { useSelfHandRaiseButtonState } from '@/hooks/useSelfRaiseHandButtonState'
import { useSelfScreenShareButtonState } from '@/hooks/useSelfScreenShareButtonState'
import { useSelfVideoButtonState } from '@/hooks/useSelfVideoButtonState'
import { useRoomStore } from '@/stores/room'
import { useUiStore } from '@/stores/ui'
import { DialogDescription, DialogTrigger } from '@radix-ui/react-dialog'
import { useRef, type PropsWithChildren } from 'react'

type DialogInCallOptionsProps = PropsWithChildren<{
  showDND?: boolean
  showMicrophone?: boolean
  showRaiseHand?: boolean
  showScreenShare?: boolean
  showVideo?: boolean
}>

export const DialogInCallOptions = ({
  children,
  showDND = true,
  showMicrophone = true,
  showRaiseHand = true,
  showScreenShare = true,
  showVideo = true,
}: DialogInCallOptionsProps) => {
  const dialogContent = useRef<HTMLDivElement | null>(null)
  const callState = useRoomStore(state => state.callState)

  const callJoined = callState === 'joined'

  const rootElementRef =
    useUiStore(state => state.rootElementRef) ?? document.body // the default fallback is document.body

  const { isDNDButtonEnabled, isDNDButtonVisible } = useSelfDNDButtonState()

  const { isRaiseHandButtonEnabled, isRaiseHandButtonVisible } =
    useSelfHandRaiseButtonState()

  const { isMicrophoneButtonVisible, isMicrophoneEnabled } =
    useSelfMicrophoneButtonState()

  const { isScreenShareButtonEnabled, isScreenShareButtonVisible } =
    useSelfScreenShareButtonState()

  const { isVideoButtonEnabled, isVideoButtonVisible } =
    useSelfVideoButtonState()

  return (
    // TODO: should this be a modal?
    <Dialog modal={false}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        // NOTE: using ! because cn() merge does not replace the custom TW classes max-w-dialog or min-h-dialog
        className="!min-h-0 !max-w-[270px] rounded-lg border-transparent bg-primary py-20 text-background"
        container={rootElementRef}
        ref={dialogContent}
        onOpenAutoFocus={e => {
          e.preventDefault()
          // Focus the dialog content to avoid auto-focusing the first button
          dialogContent.current?.focus()
        }}
      >
        <DialogHeader className="sr-only">
          <DialogTitle aria-label="Room toolbar">
            <span className="sr-only">In Call Options</span>
          </DialogTitle>
          {/* FIXME: there is an warning for aria-describedby if missing description */}
          <DialogDescription className="sr-only">
            Additional options for the current call
          </DialogDescription>
        </DialogHeader>
        <div className="flex w-full flex-col items-center justify-center gap-y-2.5">
          <div
            aria-label="Audio and video controls"
            className="inline-flex w-full justify-around gap-x-2.5 py-2.5"
            role="group"
          >
            <ButtonMicrophone
              disabled={!callJoined || !isMicrophoneEnabled}
              isVisible={isMicrophoneButtonVisible && showMicrophone}
            />
            <ButtonVideo
              disabled={!callJoined || !isVideoButtonEnabled}
              isVisible={isVideoButtonVisible && showVideo}
            />
          </div>

          <div
            aria-label="Do not disturb control"
            role="group"
            className="hidden py-2.5 has-[button]:block"
          >
            <ButtonDnD
              // TODO: implement DND button functionality
              disabled={true || !isDNDButtonEnabled}
              isVisible={isDNDButtonVisible && showDND}
            />
          </div>

          <div
            aria-label="Additional controls"
            className="inline-flex w-full justify-around gap-x-2.5 py-2.5"
            role="group"
          >
            <ButtonRaiseHand
              disabled={!callJoined || !isRaiseHandButtonEnabled}
              isVisible={isRaiseHandButtonVisible && showRaiseHand}
            />
            <ButtonShareScreen
              disabled={!callJoined || !isScreenShareButtonEnabled}
              isVisible={isScreenShareButtonVisible && showScreenShare}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
